import store from '@/store/index';
import * as swal from '@/helpers/swal';
import { toastApiError } from '@/helpers/apiToast';

/**
 * Bakim Planlama Duzenle'de (tek ve toplu) "vardiyasi uygun olanlari otomatik ata": personel ve
 * refakatci alanlari icin ayri iki dugme. Dugme yalnizca SECER; atama KAYDET'le yazilir.
 *
 * Kurallar (analiz dokumani):
 *  - Uygunluk is emri basina, is emrinin ekrandaki baslangic saatine gore; kurali sunucu uygular
 *    (WorkOrderUserBusiness.GetShiftAvailableUsers). Listedeki "Shift: Yes" ayni sey DEGIL: o
 *    vardiyanin bakimin sonuna kadar surmesini ister.
 *  - Uygun olan herkes secilir; oncelik ya da ust sinir yok.
 *  - Varsayilan olarak yalnizca o alani bos is emirleri doldurulur; dolu olan varsa sorulur,
 *    onaylanirsa uygunlar mevcutlara eklenir. Mevcut silinmez, ayni kisi iki kez eklenmez.
 *  - Ayni kisi bir is emrinde hem personel hem refakatci olamaz.
 */

export interface AutoAssignTarget {
    workOrderId: number;
    /** Ekrandaki baslangic, kayda gidecegi bicimde (ScheduledTime). */
    startDate: string;
    /** Bu alana secilebilecekler (is emrinin kendi listesi). */
    pool: any[];
    /** Bu alandaki secim. Degistirilmez; yenisi doner. */
    selected: any[];
    /** Ayni is emrinin diger alanindaki secim. */
    otherPosition: any[];
}

/** Bir is emrinin hangi baslangic icin kimlerin vardiyada oldugu. */
export interface ShiftAvailability {
    startDate: string;
    userIds: Set<number>;
}

export interface AutoAssignResult {
    /** Secimi degisen is emirleri: yeni secim. */
    selections: Map<number, any[]>;
    /** Is emri basina vardiyada olanlar; kayittaki vardiya uyarisi da kullanir. */
    availability: Map<number, ShiftAvailability>;
}

export type Position = 'master' | 'observer';

/** Sunucudaki UserPosition (1 sorumlu, 2 refakatci). */
export const positionId = (position: Position): number => store.state.enums.userPositions[position];

const hasUser = (users: any[], id: number) => users.some((user) => user.Id === id);

/**
 * Hedeflerin secimine havuzdan vardiyasi uygun olanlari ekler. Vazgecilirse, eklenecek is emri
 * kalmazsa ya da sunucuya ulasilamazsa null.
 */
export async function autoAssignShiftAvailable(vm: any, targets: AutoAssignTarget[]): Promise<AutoAssignResult | null> {
    let included = targets;
    const filled = targets.filter((target) => target.selected.length > 0);
    if (filled.length > 0) {
        const answer = await swal.check(vm,
            vm.translate('auto_assign_existing_title'),
            vm.translate('auto_assign_existing_question').replace('{0}', String(filled.length)));
        if (answer.dismiss && answer.dismiss !== 'cancel') {
            return null;
        }
        if (!answer.value) {
            included = targets.filter((target) => target.selected.length === 0);
        }
    }
    if (included.length === 0) {
        toastr.info(vm.translate('auto_assign_nothing_to_add'));
        return null;
    }

    let available: { WorkOrderId?: number, UserIds?: number[] | null }[];
    try {
        const response = await vm.$globalApiClient.workOrderUser.workOrderUserGetShiftAvailableUsers(
            included.map((target) => ({ WorkOrderId: target.workOrderId, StartDate: target.startDate })));
        available = response.data;
    } catch (e) {
        toastApiError(e);
        return null;
    }

    const result: AutoAssignResult = { selections: new Map(), availability: new Map() };
    const nobodyAvailable: number[] = [];
    included.forEach((target) => {
        const row = available.find((x) => x.WorkOrderId === target.workOrderId);
        const userIds = new Set<number>((row && row.UserIds) || []);
        result.availability.set(target.workOrderId, { startDate: target.startDate, userIds });

        const additions = target.pool.filter((user) => userIds.has(user.Id)
            && !hasUser(target.selected, user.Id)
            && !hasUser(target.otherPosition, user.Id));
        if (additions.length > 0) {
            result.selections.set(target.workOrderId, [...target.selected, ...additions]);
        } else if (target.selected.length === 0) {
            nobodyAvailable.push(target.workOrderId);
        }
    });

    if (result.selections.size > 0) {
        toastr.success(vm.translate('auto_assign_added').replace('{0}', String(result.selections.size)));
    }
    if (nobodyAvailable.length > 0) {
        toastr.warning(vm.translate('auto_assign_nobody_available').replace('{0}', nobodyAvailable.join(', ')));
    } else if (result.selections.size === 0) {
        toastr.info(vm.translate('auto_assign_nothing_to_add'));
    }
    return result;
}

/**
 * Kayittaki "vardiyasi ile eslesmiyor" uyarisi: otomatik atamanin bu baslangic icin vardiyada
 * buldugu kisi uyarilmaz - liste etiketi (IsHaveShift) bakimin tamamini istedigi icin onu
 * "Shift: No" gosterebilir. Baslangic sonradan degistiyse bulgu gecersizdir.
 */
export const isShiftMismatch = (user: any, availability: ShiftAvailability | null | undefined, startDate: string) =>
    !user.IsHaveShift && !(availability && availability.startDate === startDate && availability.userIds.has(user.Id));

/**
 * Uygun personel listesini ekranin secenegine cevirir: "Ad - Trained - Shift - Workload" metni
 * ve personel / refakatci havuzlari. Metin havuz disindakilere de yazilir; atanmis ama artik
 * atanabilir olmayan biri de secili gorunmeli, yoksa kayit onu atamadan duser.
 */
export function toUserPools(users: any[]) {
    const master: any[] = [];
    const observer: any[] = [];
    users.forEach((user) => {
        const shift = user.IsHaveShift ? 'Shift: Yes' : 'Shift: No';
        const workload = user.IsHaveWorkOrder ? 'Workload Availability: No' : 'Workload Availability: Yes';
        user.UserDisplayText = `${user.Name} - Trained: ${user.IsTrainedForMaster ? 'Yes' : 'No'} - ${shift} - ${workload}`;
        user.ObserverUserDisplayText = `${user.Name} - Trained: ${user.IsTrainedForObserver ? 'Yes' : 'No'} - ${shift} - ${workload}`;
        if (user.IsAssignableToMaster) master.push(user);
        if (user.IsAssignableToObserver) observer.push(user);
    });
    return { master, observer };
}

/**
 * Is emrinin mevcut atamalari, secenek nesneleri olarak. Secenek listedeki nesnenin kendisi
 * (vardiya, is yuku ve "calisti" bayraklari kayittaki uyarilar icin onda); listede hic olmayan
 * bir atama (pasif kullanici gibi) adiyla gosterilir, gizlenirse kayit onu duserdi.
 */
export function initialSelection(workOrderUsers: any[] | null | undefined, users: any[], position: Position) {
    return (workOrderUsers || [])
        .filter((assigned) => assigned.UserPositionId === positionId(position))
        .map((assigned) => users.find((user) => user.Id === assigned.UserId) || fallbackOption(assigned));
}

const fallbackOption = (assigned: any) => {
    const name = assigned.User ? `${assigned.User.Name} ${assigned.User.Surname}` : `#${assigned.UserId}`;
    return { Id: assigned.UserId, Name: name, UserDisplayText: name, ObserverUserDisplayText: name };
};
